import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatCurrency } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 11/03 (Josh): "On admin this should show as ISO Commissions, and this screen should show me the outstanding
// commissions I owe to ISOs." Keyed on the deal's broker CONTACT (ISOs have no logins); co-funded deals excluded.
export default function IsoOutstanding({ getAuthHeaders }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(new Set());
  const [onlyOpen, setOnlyOpen] = useState(true);
  useEffect(() => {
    axios.get(`${API}/commissions/iso-outstanding`, { headers: getAuthHeaders() })
      .then(r => setData(r.data)).catch(() => setError('Could not load ISO commissions'));
  }, [getAuthHeaders]);
  const toggle = id => setOpen(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return <p className="text-sm text-slate-500">Loading ISO commissions…</p>;
  const isos = onlyOpen ? data.isos.filter(g => g.outstanding > 0.005) : data.isos;
  return (
    <Card className="mb-6" data-testid="iso-outstanding">
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg"><Wallet className="h-5 w-5 text-red-600" /> Outstanding commissions owed to ISOs</CardTitle>
          <label className="text-xs text-slate-600 flex items-center gap-1"><input type="checkbox" checked={onlyOpen} onChange={e => setOnlyOpen(e.target.checked)} /> only ISOs still owed</label>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-2 text-sm">
          <div><div className="text-xs text-slate-500">Scheduled</div><div className="font-semibold">{formatCurrency(data.totals.owed)}</div></div>
          <div><div className="text-xs text-slate-500">Paid</div><div className="font-semibold text-emerald-700">{formatCurrency(data.totals.paid)}</div></div>
          <div><div className="text-xs text-slate-500">Outstanding</div><div className="font-bold text-red-600" data-testid="iso-outstanding-total">{formatCurrency(data.totals.outstanding)}</div></div>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">{data.note}</p>
      </CardHeader>
      <CardContent className="p-0">
        {isos.length === 0 && <p className="px-4 pb-4 text-sm text-slate-500">Nothing outstanding.</p>}
        {isos.map(g => (
          <div key={g.contact_id} className="border-t">
            <button type="button" className="w-full flex items-center justify-between px-4 py-2 hover:bg-slate-50 text-left" onClick={() => toggle(g.contact_id)}>
              <span className="flex items-center gap-2">{open.has(g.contact_id) ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                <Link to={`/contacts/${g.contact_id}`} className="font-medium text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>{g.name}</Link>
                <span className="text-xs text-slate-500">{g.deals.length} deal{g.deals.length === 1 ? '' : 's'}</span></span>
              <span className="text-sm"><span className="text-slate-500 mr-3">paid {formatCurrency(g.paid)}</span><b className={g.outstanding > 0 ? 'text-red-600' : 'text-emerald-700'}>{formatCurrency(g.outstanding)}</b></span>
            </button>
            {open.has(g.contact_id) && (
              <table className="w-full text-sm mb-2">
                <thead><tr className="text-xs text-slate-500"><th className="text-left pl-10 py-1">Deal</th><th className="text-left">Funded</th><th className="text-right">Scheduled</th><th className="text-right">Paid</th><th className="text-right pr-4">Outstanding</th></tr></thead>
                <tbody>
                  {g.deals.filter(d => !onlyOpen || d.outstanding > 0.005).map(d => (
                    <tr key={d.deal_uuid} className="border-t">
                      <td className="pl-10 py-1"><Link to={`/deals/${d.deal_uuid}`} className="text-blue-600 hover:underline">{d.deal_number}</Link> <span className="text-slate-700">{d.deal_name}</span> {d.status && <Badge variant="outline" className="ml-1 text-[10px]">{d.status}</Badge>}</td>
                      <td className="text-slate-500">{d.funding_date ? String(d.funding_date).slice(0, 10) : '—'}</td>
                      <td className="text-right">{formatCurrency(d.owed)}</td>
                      <td className="text-right text-emerald-700">{formatCurrency(d.paid)}</td>
                      <td className={`text-right pr-4 font-medium ${d.outstanding > 0 ? 'text-red-600' : ''}`}>{formatCurrency(d.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
